import type { ErrorEnvelope, FieldError } from './schema.js';

/**
 * Typed errors for the §4.9 envelope. Screens branch on `code` only and show `messageFa`; the
 * `requestId` is shown (copyable) for unresolved errors (FTD §11, §14).
 */

/** Codes the catalog marks retryable with the same request and key (AC §13). */
export const RETRYABLE_ERROR_CODES: ReadonlySet<string> = new Set([
  'IDEMPOTENCY_REQUEST_IN_PROGRESS',
  'RATE_LIMIT_EXCEEDED',
  'SERVICE_UNAVAILABLE',
  'AUTH_PROVIDER_UNAVAILABLE',
  'BUSINESS_CALENDAR_UNAVAILABLE',
  'PAYMENT_PROVIDER_UNAVAILABLE',
  'PAYMENT_IN_PROGRESS',
  'SETTLEMENT_PROVIDER_UNAVAILABLE',
]);

/**
 * Codes the catalog marks "do not retry" although their status is 5xx (AC §13):
 * `CONFIRMATION_INTEGRITY_ERROR` means the stored snapshot fails its hash, so the same request can
 * never succeed; report the `requestId` instead.
 */
export const NON_RETRYABLE_ERROR_CODES: ReadonlySet<string> = new Set([
  'CONFIRMATION_INTEGRITY_ERROR',
]);

/** Status-class fallbacks when a response is not an envelope (for example a proxy error page). */
const FALLBACK_BY_STATUS: Readonly<Record<number, { code: string; messageFa: string }>> = {
  400: { code: 'REQUEST_INVALID', messageFa: 'درخواست نامعتبر است.' },
  401: { code: 'AUTH_TOKEN_INVALID', messageFa: 'برای امنیت حساب، دوباره وارد شوید.' },
  403: { code: 'ACCESS_DENIED', messageFa: 'به این بخش دسترسی ندارید.' },
  404: { code: 'RESOURCE_NOT_FOUND', messageFa: 'مورد درخواستی پیدا نشد.' },
  409: { code: 'STATE_CONFLICT', messageFa: 'وضعیت تغییر کرده است. صفحه را به‌روز کنید.' },
  422: { code: 'BUSINESS_RULE_VIOLATION', messageFa: 'این درخواست قابل انجام نیست.' },
  429: { code: 'RATE_LIMIT_EXCEEDED', messageFa: 'تعداد درخواست‌ها زیاد است. کمی بعد تلاش کنید.' },
  503: { code: 'SERVICE_UNAVAILABLE', messageFa: 'سرویس موقتاً در دسترس نیست.' },
};

const INTERNAL_FALLBACK = {
  code: 'INTERNAL_ERROR',
  messageFa: 'مشکلی پیش آمد. لطفاً دوباره تلاش کنید.',
};

function fallbackFor(status: number): { code: string; messageFa: string } {
  return (
    FALLBACK_BY_STATUS[status] ?? (status >= 500 ? INTERNAL_FALLBACK : FALLBACK_BY_STATUS[400]!)
  );
}

export interface ApiErrorInit {
  readonly code: string;
  readonly details?: Readonly<Record<string, unknown>>;
  readonly fieldErrors?: readonly FieldError[];
  readonly messageFa: string;
  readonly requestId: string | null;
  readonly retryAfterSeconds?: number;
  readonly status: number;
}

/** An HTTP error answered by the API (or synthesized from a non-envelope error response). */
export class ApiError extends Error {
  readonly code: string;
  readonly details: Readonly<Record<string, unknown>> | undefined;
  readonly fieldErrors: readonly FieldError[];
  readonly messageFa: string;
  readonly requestId: string | null;
  readonly retryAfterSeconds: number | undefined;
  readonly status: number;

  constructor(init: ApiErrorInit) {
    super(`${init.status} ${init.code}`);
    this.name = 'ApiError';
    this.code = init.code;
    this.details = init.details;
    this.fieldErrors = init.fieldErrors ?? [];
    this.messageFa = init.messageFa;
    this.requestId = init.requestId;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.status = init.status;
  }

  /** True when the same request (same key) may succeed later without change (AC §13). */
  get retryable(): boolean {
    if (NON_RETRYABLE_ERROR_CODES.has(this.code)) {
      return false;
    }
    return RETRYABLE_ERROR_CODES.has(this.code) || this.status >= 500;
  }
}

/** No connectivity for a query: the device is offline or the host is unreachable. */
export class NetworkError extends Error {
  readonly code = 'NETWORK_UNAVAILABLE';
  readonly messageFa = 'اینترنت وصل نیست. اتصال اینترنت را بررسی کنید.';

  constructor(options?: { cause?: unknown }) {
    super('Network unavailable', options);
    this.name = 'NetworkError';
  }
}

/**
 * A command whose outcome is unknown (timeout or lost connection after sending). It is not a failure
 * (FTD §2.4): the UI offers "Check status", which queries the resource or retries with the **same**
 * idempotency key, never a new one.
 */
export class UnknownResultError extends Error {
  readonly code = 'UNKNOWN_RESULT';
  readonly messageFa = 'نتیجه درخواست مشخص نیست. پیش از تلاش دوباره، وضعیت را بررسی کنید.';

  constructor(
    readonly idempotencyKey: string,
    options?: { cause?: unknown },
  ) {
    super('Command result unknown', options);
    this.name = 'UnknownResultError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function isFieldError(value: unknown): value is FieldError {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.field === 'string' &&
    typeof candidate.code === 'string' &&
    typeof candidate.messageFa === 'string'
  );
}

/** Reads a §4.9 envelope; anything else is `undefined`. */
export function parseErrorEnvelope(body: unknown): ErrorEnvelope['error'] | undefined {
  if (body === null || typeof body !== 'object') {
    return undefined;
  }
  const error = (body as { error?: unknown }).error;
  if (error === null || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as Record<string, unknown>;
  if (typeof candidate.code !== 'string' || typeof candidate.messageFa !== 'string') {
    return undefined;
  }

  const details =
    candidate.details !== null && typeof candidate.details === 'object'
      ? (candidate.details as Record<string, unknown>)
      : undefined;

  return {
    code: candidate.code,
    fieldErrors: Array.isArray(candidate.fieldErrors)
      ? candidate.fieldErrors.filter(isFieldError)
      : [],
    messageFa: candidate.messageFa,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : '',
    ...(details === undefined ? {} : { details }),
  };
}

/** `Retry-After` in seconds (delta-seconds or HTTP-date), or `undefined`. */
export function parseRetryAfter(
  value: string | null,
  now: number = Date.now(),
): number | undefined {
  if (value === null || value.trim() === '') {
    return undefined;
  }
  if (/^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  const date = Date.parse(value);
  return Number.isNaN(date) ? undefined : Math.max(0, Math.ceil((date - now) / 1000));
}

/** Builds the typed error from a failed response and its (possibly non-JSON) body. */
export function toApiError(
  status: number,
  body: unknown,
  headers: { get(name: string): string | null },
): ApiError {
  const envelope = parseErrorEnvelope(body);
  const headerRequestId = headers.get('x-request-id');
  const fallback = fallbackFor(status);
  const detailsRetry = envelope?.details?.retryAfterSeconds;
  const retryAfterSeconds =
    parseRetryAfter(headers.get('retry-after')) ??
    (typeof detailsRetry === 'number' ? Math.ceil(detailsRetry) : undefined);

  return new ApiError({
    code: envelope?.code ?? fallback.code,
    fieldErrors: envelope?.fieldErrors ?? [],
    messageFa: envelope?.messageFa ?? fallback.messageFa,
    requestId: envelope?.requestId || headerRequestId,
    status,
    ...(envelope?.details === undefined ? {} : { details: envelope.details }),
    ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
  });
}

/** Groups `fieldErrors` by field path for form libraries (React Hook Form `setError`). */
export function fieldErrorsByPath(error: unknown): Record<string, FieldError[]> {
  if (!isApiError(error)) {
    return {};
  }
  const grouped: Record<string, FieldError[]> = {};
  for (const fieldError of error.fieldErrors) {
    (grouped[fieldError.field] ??= []).push(fieldError);
  }
  return grouped;
}
